import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { MeshGradient } from '@paper-design/shaders-react';
import { auraColorsForTheme, auraRadialColorForTheme, type AuraTheme, type ThinkingAuraSettings } from './thinkingAura';

const VERTEX = `#version 300 es
in vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

// A compact, transparent curtain shader adapted for the shallow message row.
// This is our own implementation of layered waving light bands, not a copy of
// the full-screen FutureUI component.
const FRAGMENT = `#version 300 es
precision highp float;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_time;
uniform float u_waviness;
uniform float u_brightness;
uniform vec3 u_color1;
uniform vec3 u_color2;
out vec4 fragColor;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + 1.0), f.x), f.y);
}
void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 p = uv - 0.5;
  p.x *= u_resolution.x / u_resolution.y;
  p += (u_mouse - 0.5) * vec2(0.32, 0.12);
  vec3 light = vec3(0.0);
  float veil = 0.0;
  for (int i = 0; i < 5; i++) {
    float layer = float(i);
    float drift = u_time * (0.26 + layer * 0.07);
    float wave = sin(p.x * (2.2 + layer * 0.3) + drift + layer * 1.7);
    wave += 0.38 * sin(p.x * 5.3 - drift * 0.8 + layer * 2.1);
    wave += 0.65 * (noise(vec2(p.x * 1.8 + layer, drift * 0.5)) - 0.5);
    float center = 0.14 + wave * u_waviness * 0.16 - layer * 0.07;
    float width = 0.15 + layer * 0.025;
    float band = exp(-pow((p.y - center) / width, 2.0));
    float fold = 0.45 + 0.55 * sin(p.x * 4.2 + drift + layer * 1.4);
    fold *= 0.7 + 0.3 * noise(vec2(p.x * 6.0 + layer, drift));
    float strength = band * fold * (0.42 / (1.0 + layer * 0.24));
    vec3 tint = mix(u_color1, u_color2, clamp(uv.y + layer * 0.13, 0.0, 1.0));
    light += tint * strength;
    veil += strength;
  }
  float edge = smoothstep(0.0, 0.14, uv.x) * smoothstep(0.0, 0.14, 1.0 - uv.x);
  vec3 color = 1.0 - exp(-light * u_brightness * 2.4);
  float alpha = clamp(veil * u_brightness * 0.66, 0.0, 0.9) * edge;
  fragColor = vec4(color, alpha);
}`;

function rgb(hex: string): Float32Array {
  return new Float32Array([1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16) / 255));
}

function readChromeTheme(): AuraTheme {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

function useChromeTheme(): AuraTheme {
  const [theme, setTheme] = useState<AuraTheme>(readChromeTheme);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setTheme(readChromeTheme()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
}

function shader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const result = gl.createShader(type);
  if (!result) return null;
  gl.shaderSource(result, source);
  gl.compileShader(result);
  if (gl.getShaderParameter(result, gl.COMPILE_STATUS)) return result;
  gl.deleteShader(result);
  return null;
}

function AuroraCurtain({ settings, reducedMotion, colors }: { settings: ThinkingAuraSettings; reducedMotion: boolean; colors: readonly [string, string, string, string] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const settingsRef = useRef(settings);
  const colorsRef = useRef(colors);
  const redrawRef = useRef<(() => void) | null>(null);
  settingsRef.current = settings;
  colorsRef.current = colors;

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext('webgl2', { alpha: true, antialias: false, premultipliedAlpha: false });
    if (!canvas || !gl) return;
    const vertex = shader(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = shader(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vertex || !fragment) {
      if (vertex) gl.deleteShader(vertex);
      if (fragment) gl.deleteShader(fragment);
      return;
    }
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { gl.deleteProgram(program); return; }
    const buffer = gl.createBuffer();
    if (!buffer) { gl.deleteProgram(program); return; }
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const attribute = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(attribute);
    gl.vertexAttribPointer(attribute, 2, gl.FLOAT, false, 0, 0);
    const resolution = gl.getUniformLocation(program, 'u_resolution');
    const mouse = gl.getUniformLocation(program, 'u_mouse');
    const time = gl.getUniformLocation(program, 'u_time');
    const waviness = gl.getUniformLocation(program, 'u_waviness');
    const brightness = gl.getUniformLocation(program, 'u_brightness');
    const color1 = gl.getUniformLocation(program, 'u_color1');
    const color2 = gl.getUniformLocation(program, 'u_color2');
    const pointer = { x: 0.5, y: 0.5 };
    const host = canvas.closest('.dock-row') ?? canvas.parentElement;
    const onPointer = (event: Event) => {
      if (!settingsRef.current.interactive || !(event instanceof PointerEvent) || !host) return;
      const bounds = host.getBoundingClientRect();
      pointer.x = (event.clientX - bounds.left) / bounds.width;
      pointer.y = 1 - (event.clientY - bounds.top) / bounds.height;
    };
    host?.addEventListener('pointermove', onPointer);
    let frame = 0;
    let visible = true;
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible && reducedMotion) redrawRef.current?.();
    });
    observer.observe(canvas);
    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(650000 / Math.max(bounds.width * bounds.height, 1)));
      canvas.width = Math.max(1, Math.round(bounds.width * ratio));
      canvas.height = Math.max(1, Math.round(bounds.height * ratio));
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    resize();
    const draw = (stamp: number) => {
      if (visible && !document.hidden && !gl.isContextLost()) {
        const current = settingsRef.current;
        gl.uniform2f(resolution, canvas.width, canvas.height);
        gl.uniform2f(mouse, current.interactive ? pointer.x : 0.5, current.interactive ? pointer.y : 0.5);
        gl.uniform1f(time, stamp * 0.001 * (reducedMotion ? 0 : current.speed));
        gl.uniform1f(waviness, current.waviness);
        gl.uniform1f(brightness, current.brightness);
        gl.uniform3fv(color1, rgb(colorsRef.current[0]));
        gl.uniform3fv(color2, rgb(colorsRef.current[1]));
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      if (!reducedMotion) frame = requestAnimationFrame(draw);
    };
    redrawRef.current = () => draw(performance.now());
    frame = requestAnimationFrame(draw);
    return () => {
      redrawRef.current = null;
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      host?.removeEventListener('pointermove', onPointer);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, [reducedMotion]);

  useEffect(() => { if (reducedMotion) redrawRef.current?.(); }, [settings, colors, reducedMotion]);

  return <canvas ref={canvasRef} className="thinking-aura-canvas" aria-hidden="true" />;
}

export function ThinkingAuraEngine({ settings, reducedMotion, theme: themeOverride }: { settings: ThinkingAuraSettings; reducedMotion: boolean; theme?: AuraTheme }) {
  const chromeTheme = useChromeTheme();
  const theme = themeOverride ?? chromeTheme;
  const colors = useMemo(() => auraColorsForTheme(settings, theme), [
    settings.color1, settings.color2, settings.color3, settings.color4,
    settings.lightColor1, settings.lightColor2, settings.lightColor3, settings.lightColor4,
    theme,
  ]);
  const radialColor = auraRadialColorForTheme(settings, theme);
  if (!settings.enabled) return null;
  const style = {
    '--thinking-aura-opacity': settings.opacity,
    '--thinking-radial-color': radialColor,
    '--thinking-radial-opacity': settings.radialOpacity,
    '--thinking-radial-size': `${settings.radialSize}%`,
    '--thinking-radial-x': `${settings.radialX}%`,
    '--thinking-radial-y': `${settings.radialY}%`,
  } as CSSProperties;
  return (
    <div className={`thinking-aura-backdrop thinking-aura-backdrop-${settings.engine} thinking-aura-theme-${theme}`} style={style} aria-hidden="true">
      <div className="thinking-aura-overscan">
        {settings.engine === 'mesh' ? (
          <MeshGradient
            width="100%" height="100%" fit="cover"
            colors={colors}
            distortion={settings.distortion} swirl={settings.swirl}
            grainMixer={settings.grain} grainOverlay={settings.grain * 0.5}
            speed={reducedMotion ? 0 : settings.speed}
            minPixelRatio={1} maxPixelCount={650000}
            className="thinking-aura-mesh"
          />
        ) : <AuroraCurtain settings={settings} reducedMotion={reducedMotion} colors={colors} />}
      </div>
      {settings.engine === 'mesh' && settings.radialEnabled ? <div className="thinking-aura-radial" /> : null}
    </div>
  );
}
